import { z } from "zod/v3";

const emailSchema = z.string().email().max(320);

/** Пустое значение допустимо: реквизит необязателен. */
export function validateInn(value: string): string | null {
  const inn = value.trim();
  if (inn === "") return null;
  return /^\d{10}(\d{2})?$/.test(inn)
    ? null
    : "ИНН состоит из 10 цифр для организации или 12 для ИП";
}

export function validateKpp(value: string): string | null {
  const kpp = value.trim();
  if (kpp === "") return null;
  return /^\d{9}$/.test(kpp) ? null : "КПП состоит из 9 цифр";
}

export function validateEmail(value: string): string | null {
  const email = value.trim();
  if (email === "") return "Укажите email";
  return emailSchema.safeParse(email).success
    ? null
    : "Некорректный email: проверьте, что есть имя, «@» и домен";
}

/**
 * Домен приводится к виду, в котором его хранит Core: строчными буквами, без
 * пробелов и точек по краям. Ведущее «@» убираем: его любят вставлять из адреса.
 */
export function normalizeDomain(value: string): string {
  return value
    .trim()
    .replace(/^@+/, "")
    .replace(/^\.+|\.+$/g, "")
    .toLowerCase();
}

export function validateDomain(value: string): string | null {
  const domain = normalizeDomain(value);
  if (domain === "") return "Укажите домен";
  if (domain.length > 255) return "Домен длиннее 255 символов";
  if (/[\s@/\\,;]/.test(domain) || !domain.includes(".")) {
    return "Домен указывается без «@» и пробелов, например example.ru";
  }
  return null;
}

export interface InvalidAddress {
  /** Адрес в том виде, в каком его ввёл человек. */
  readonly address: string;
  /** Номер адреса в списке, с единицы: пустые пункты не считаются. */
  readonly position: number;
}

export interface AddressList {
  readonly valid: readonly string[];
  readonly invalid: readonly InvalidAddress[];
}

/** Разбор поля со списком адресов через запятую. Пустые пункты пропускаются. */
export function splitAddresses(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function checkAddresses(value: string): AddressList {
  const valid: string[] = [];
  const invalid: InvalidAddress[] = [];
  splitAddresses(value).forEach((address, index) => {
    if (emailSchema.safeParse(address).success) valid.push(address);
    else invalid.push({ address, position: index + 1 });
  });
  return { valid, invalid };
}

/**
 * Сообщение, которое прямо называет неверный адрес: человеку не нужно искать
 * опечатку глазами в длинном списке.
 */
export function addressListError(
  value: string,
  fieldLabel: string,
  { required }: { required: boolean },
): string | null {
  const { valid, invalid } = checkAddresses(value);
  if (invalid.length === 1) {
    const [only] = invalid;
    return `Некорректный email в поле «${fieldLabel}»: «${only?.address ?? ""}» (адрес №${only?.position ?? 0}). Исправьте его или удалите.`;
  }
  if (invalid.length > 1) {
    const names = invalid
      .map((item) => `«${item.address}» (№${item.position})`)
      .join(", ");
    return `Некорректные email в поле «${fieldLabel}»: ${names}. Исправьте их или удалите.`;
  }
  if (required && valid.length === 0) {
    return `Укажите хотя бы один email в поле «${fieldLabel}»`;
  }
  return null;
}

/**
 * Добавляет адреса к уже введённому тексту, ничего не стирая. Уже
 * присутствующие адреса (без учёта регистра) повторно не добавляются.
 * Неверные пункты, введённые вручную, остаются на месте: их исправит человек.
 */
export function mergeAddresses(
  existing: string,
  additions: readonly string[],
): string {
  const items = splitAddresses(existing);
  const seen = new Set(items.map((item) => item.toLowerCase()));
  for (const address of additions) {
    const key = address.trim().toLowerCase();
    if (key === "" || seen.has(key)) continue;
    seen.add(key);
    items.push(address.trim());
  }
  return items.join(", ");
}
